import AccessDeniedIcon from "@assets/icons/new/access-denied_566px.svg";
import { Box, Link, Stack } from "@mui/material";
import { contactSupport } from "@utils/email-utils";
import { ContactSupportLocations } from "@custom-types/types";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import {
  DEFAULT_BOLD_TEXT_FONT_WEIGHT,
  DEFAULT_TITLE_FONT_SIZE,
} from "@styles/common-styles";
import { MainPageLayout } from "@components/page-layout/main-page-layout";
import {
  sdbCompanyIdsSelector,
  subscriptionStatusSelector,
} from "@store/sdb-company/sdb-company-selector";
import { Button, Text, LoadingScreen, neutral } from "@flat-ui";
import { useAppSelector } from "@store/store-helper";
import { FaroTextButton } from "@components/common/faro-text-button";
import ArrowLeftIcon from "@assets/icons/new/arrow-left_18px.svg?react";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { PlaceholderWrapper } from "@components/common/placeholder-wrapper";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useSubscriptionExpiredPage } from "@pages/subscription-expired/use-subscription-expired-page";
import { appStateSelector } from "@store/app/app-selector";
import { useEffect } from "react";
import { useAppParams } from "@router/router-helper";
import { assert } from "@faro-lotv/foundation";

/** Subscription expired page */
export function SubscriptionExpiredPage(): JSX.Element {
  const { trackEvent } = useTrackEvent();

  const sdbCompanyIds = useAppSelector(sdbCompanyIdsSelector);
  const { companyId } = useAppParams();
  const subscriptionStatus = useAppSelector(subscriptionStatusSelector);
  const { canViewProductDetails } = useHasUserValidRoleCompanyLevel();

  const { navigateToRoot, navigateToProjects } = useAppNavigation();

  const { isBetaTestingEnabled } = useAppSelector(appStateSelector);

  // TODO: Remove beta testing: https://faro01.atlassian.net/browse/ST-3626
  // If subscription is active, navigate to root or projects page based on companyId
  useEffect(() => {
    if (
      !isBetaTestingEnabled ||
      subscriptionStatus === "subscription-active" ||
      subscriptionStatus === "trial-active"
    ) {
      if (companyId) {
        navigateToProjects({ companyId });
      } else {
        navigateToRoot();
      }
    }
  }, [
    isBetaTestingEnabled,
    navigateToRoot,
    navigateToProjects,
    subscriptionStatus,
    companyId,
  ]);

  // We are sure that subscriptionStatus is either "subscription-ended" or "trial-ended" here. If not, we want to fail early
  assert(
    subscriptionStatus === "subscription-ended" ||
      subscriptionStatus === "trial-ended",
    `Invalid status: ${subscriptionStatus}. The code should not reach here.`
  );

  const {
    canApplyRetentionOffer,
    subtitle,
    retentionOfferDuration,
    onRetentionRequested,
    isLoading,
  } = useSubscriptionExpiredPage({
    canViewProductDetails,
    subscriptionStatus,
  });

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <MainPageLayout
      shouldShowSidebar={false}
      shouldUseFullBodyLayout={true}
      shouldShowLogo={true}
      shouldShowUserMenu={true}
      shouldShowProgressOverviewMenu={false}
    >
      <Stack
        alignItems="center"
        justifyContent="space-between"
        textAlign="center"
        sx={{
          px: 2,
          minHeight: "calc(80vh - 52px)",
          overflow: "hidden",
          mb: "60px",
        }}
        spacing={3}
      >
        {/* Icon */}
        <Box
          component="img"
          src={AccessDeniedIcon}
          sx={{
            width: "100%",
            maxWidth: "565px",

            mb: "32px",
          }}
        />

        {/* Title */}
        <Text
          variant="heading20"
          sx={{
            color: neutral[600],

            mb: "20px",
            fontSize: "38px",
          }}
        >
          {subscriptionStatus === "subscription-ended"
            ? "Subscription expired"
            : "Trial expired"}
        </Text>

        {/* Subtitle */}
        <Text
          variant="bodyL"
          sx={{
            color: neutral[600],
            maxWidth: "880px",
            fontSize: "20px",
          }}
        >
          {subtitle}
        </Text>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={5}
          textAlign="center"
          sx={{
            my: "100px",
          }}
        >
          {sdbCompanyIds.length > 1 && (
            <FaroTextButton
              startIcon={<ArrowLeftIcon />}
              onClick={navigateToRoot}
              sx={{ fontSize: "16px", fontWeight: 600 }}
            >
              Back to Workspaces
            </FaroTextButton>
          )}
          <PlaceholderWrapper
            isVisible={canViewProductDetails && canApplyRetentionOffer}
            shouldPreserveWidth={false}
          >
            <Button
              sx={{
                width: "162px",
                height: "24px",
                fontSize: DEFAULT_TITLE_FONT_SIZE,
                fontWeight: DEFAULT_BOLD_TEXT_FONT_WEIGHT,
                boxSizing: "content-box",
              }}
              onClick={onRetentionRequested}
            >
              Continue {retentionOfferDuration}-day trial
            </Button>
          </PlaceholderWrapper>
        </Stack>

        {/* Support Link */}
        <PlaceholderWrapper isVisible={canViewProductDetails}>
          <Text variant="bodyM">
            Need help? Contact{" "}
            <Link
              onClick={() => {
                contactSupport({
                  location: ContactSupportLocations.subscriptionExpiredPage,
                  trackEvent,
                });
              }}
              aria-label="Contact support"
              sx={{
                cursor: "pointer",
                textDecoration: "none",
              }}
            >
              support
            </Link>{" "}
            for assistance with billing or plan options.
          </Text>
        </PlaceholderWrapper>
      </Stack>
    </MainPageLayout>
  );
}
